/* eslint-disable no-console */
import axios from "axios";
import moment from "moment";
import nextCookie from "next-cookies";
import { AppProps } from "next/app";
import { NextComponentType, NextPageContext } from "next/dist/shared/lib/utils";
import Head from "next/head";
import Router from "next/router";
import NProgress from "nprogress";
import React, { useEffect, useRef } from "react";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { Store } from "redux";

import { listTravellers } from "shared/cbt/api/travellers";
import { usersFetched } from "shared/data/actions/cbt";
import { setFoodData } from "shared/data/actions/hotels";
import { setRequestorConfig } from "shared/data/actions/requestorConfig";
import {
  getGOLAPIUrl,
  loadAirlines,
  loadCountries,
  loadWebRequestorDetails,
  setAirlines,
  setCountries,
} from "shared/data/actions/storage";
import { logoutUser, setUserValues } from "shared/data/actions/user";
import {
  getCbtToken,
  logoutCustomer,
  setCustomerDevice,
  setCustomerToken,
} from "shared/data/customerTokens";
import detailCustomer2 from "shared/gol-api/detailCustomer2";
import { getFood } from "shared/gol-api/exportFood";
import { getForwardedHeadersFromRequest } from "shared/lib/forwardedHeaders";
import { getRequestorClientId } from "shared/lib/functions";
// @ts-ignore - Ignore the module error
import { POSSIBLE_LANGUAGES } from "shared/lib/languages";
import { getInitialClientId, setLocale } from "shared/lib/requestorFunctions";
// @ts-ignore
import Logger from "shared/services/Logger";
// @ts-ignore
import { getDomainForFileServer } from "shared/services/requestorConfiguration";

import LoginForm from "@components/Login/Form";
import ErrorLayout from "@components/UI/Layouts/ErrorLayout";
import StandardLayout from "@components/UI/Layouts/StandardLayout";

import { pageDataLayer } from "@lib/dataLayers";
import { shouldRenderFileServerPageNotFound } from "@lib/fileServerPages";
import withReduxStore from "@lib/with-redux-store";
import * as Sentry from "@sentry/browser";
import { ROUTES } from "@shared/constants";

import useMandatoryLoginSession from "../hooks/useMandatoryLoginSession";
import { timeBenchmark } from "../lib/benchmarks";
import {
  createFileServerIndexUrl,
  fetchFileServerData,
} from "../lib/fileServer";
import { getRuntimeEnvValue } from "../lib/runtimeEnv";
import { shouldEnableSpyTester } from "../lib/spyTester";
import { appCache } from "../services/AppCache";
import "../styles/main.scss";

const config = require("shared/config.json");
const { getEnvironmentFromRequestorClientId } = require("shared/config/utils");
const { beforeSend, SENTRY_IGNORE_ERRORS } = require("../config/sentry.js");
const {
  generateRandomString,
  passwordProtection,
} = require("../lib/serverHelpers.ts");

Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

const DEFAULT_LANG_GLOBAL = "cs";

function getLocalStaticCssUrl() {
  try {
    const staticIndex = require("../static/index.json");
    const cssFileName = Object.keys(staticIndex?.css || {})[0];

    return cssFileName ? `/_next/static/css/${cssFileName}` : null;
  } catch (e) {
    return null;
  }
}

function resolveFileServerDataForRuntime(fileServerData, domain = "") {
  if (!fileServerData) {
    return fileServerData;
  }

  if (!domain.includes("localhost") && !domain.includes("d4:")) {
    return fileServerData;
  }

  const localStaticCssUrl = getLocalStaticCssUrl();

  return localStaticCssUrl
    ? { ...fileServerData, "styles.css": localStaticCssUrl }
    : fileServerData;
}

const errorRoutes = ["/404", "/_error"];

Sentry.init({
  dsn: config.sentryUrl,
  environment: getEnvironmentFromRequestorClientId().label,
  ignoreErrors: SENTRY_IGNORE_ERRORS,
  beforeSend,
});

interface IPageProps {
  domain?: string;
  fileServerPagesToDownload?: string[];
  gtm?: {
    gtmId?: any;
    nonce?: string;
  };
  downloadedPages?: Record<string, any>;
  pageNotFound?: boolean;
}

type ExtendedComponent = NextComponentType<NextPageContext, any, any> & {
  getLayout?: (page: React.ReactElement, nonce?: string) => React.ReactNode;
};

interface IAppProps {
  reduxStore: Store;
  pageProps: IPageProps;
  randomString: string;
  locale: string;
  messages: Record<string, any>;
  now: number;
}

async function fetchWithCache(cacheKey, lang, ttl, fetchFunction, domain) {
  if (!lang) {
    return null;
  }

  if (
    appCache[cacheKey] &&
    appCache[cacheKey][lang] &&
    Date.now() - appCache[cacheKey][lang].timestamp < ttl
  ) {
    console.log(
      `Cache - (hit) ${cacheKey} for ${lang} expire at: ${new Date(
        appCache[cacheKey][lang].timestamp + ttl
      ).toLocaleString()}`
    );

    return appCache[cacheKey][lang].data;
  }

  console.log(`Cache - (miss) Fetching new data for ${cacheKey} for ${lang}`);
  const data = await timeBenchmark(
    async () => fetchFunction(),
    null,
    `Fetching data for ${cacheKey} for ${lang} (${domain})`
  );

  if (!appCache[cacheKey]) {
    appCache[cacheKey] = {};
  }

  appCache[cacheKey][lang] = {
    data,
    timestamp: Date.now(),
  };

  console.log(
    `Cache - stored ${cacheKey} for ${lang} expire at: ${new Date(
      appCache[cacheKey][lang].timestamp + ttl
    ).toLocaleString()}`
  );

  return data;
}

const transformAlbanianLanguageShortcut = (lang: string) => {
  return lang === "al" ? "sq" : lang;
};

const prepareCbtUsers = async ({ reduxStore }) => {
  const cbtToken = await getCbtToken();

  const { frontendSettings } = reduxStore.getState().storage;

  const allUsers = await listTravellers({
    cbtToken,
    customerUsername: reduxStore.getState().user.email,
    cbtApiUrl: frontendSettings.dealerCorporateSettings?.cbtApiUrl,
    limit: 1000,
  });

  if (allUsers?.success) {
    const enhancedUsers = allUsers.data.data.map((user) => ({
      ...user,
      value: user?.Id,
      label: `${user?.FirstName} ${user?.LastName}`,
    }));

    await reduxStore.dispatch(
      usersFetched({
        success: allUsers.success,
        data: { data: enhancedUsers },
      })
    );
  }
};

function MyApp({
  Component,
  pageProps,
  router,
  reduxStore,
  randomString,
  messages,
}: AppProps & IAppProps) {
  const TypedComponent = Component as ExtendedComponent;
  const documentNonce = useRef(pageProps?.gtm?.nonce).current;

  useEffect(() => {
    const fetchFoodData = async () => {
      const { food } = reduxStore.getState().hotels;
      if (
        (router.route === ROUTES.HOTEL || router.route === ROUTES.HOTELS) &&
        (!food || food.length === 0)
      ) {
        try {
          const response = await getFood();
          if (response.success) {
            reduxStore.dispatch(setFoodData(response.data.FoodType));
          }
        } catch (error) {
          console.error("Failed to fetch food data", error);
        }
      }
    };

    fetchFoodData();
  }, [router.route, reduxStore]);

  useEffect(() => {
    const lang = reduxStore.getState().requestorConfig.currentLanguage;

    const transformedLang = transformAlbanianLanguageShortcut(lang);
    moment.locale(transformedLang);
    setLocale(transformedLang);

    const { user, storage } = reduxStore.getState();

    const isCbtUser =
      storage?.frontendSettings.dealerCorporateSettings?.enableCbt === "true";

    if (user?.isLoggedIn && isCbtUser) {
      prepareCbtUsers({ reduxStore });
    }

    // @ts-ignore - Cypress adds store to window for testing
    if (window.Cypress) {
      // @ts-ignore
      window.store = reduxStore;
    }

    const requestorClientId =
      process.env.NEXT_PUBLIC_D4_requestorClientId ||
      config.requestorClientId ||
      window.location.hostname;
    // @ts-ignore - spyTester is a global analytics tool
    if (
      shouldEnableSpyTester(requestorClientId, window.location.hostname) &&
      window.spyTester
    ) {
      // @ts-ignore
      window.spyTester.init({ idSite: "tpd4" });
    }
    if (router.route?.includes(`${ROUTES.RESERVATION}/`)) {
      pageDataLayer("other", user);
    } else {
      pageDataLayer("home", user);
    }
  }, [reduxStore, router]);

  const anonymousSearchSetting = reduxStore.getState().storage.frontendSettings
    .dealerCorporateSettings?.enableAnonymousSearch;
  const isAnonymousSearchEnabled = anonymousSearchSetting === "true";
  const isMandatoryLoginRequired = anonymousSearchSetting === "false";

  useMandatoryLoginSession({
    isMandatoryLoginRequired,
    language: router.query.lang,
    reduxStore,
  });

  const isLoginIframe =
    (router.route.includes(ROUTES.IFRAME) ||
      router.route.includes(ROUTES.LOW_COST_SEARCH)) &&
    isAnonymousSearchEnabled === false;

  return (
    <Provider store={reduxStore}>
      <IntlProvider
        locale="cs"
        messages={reduxStore.getState().requestorConfig.textStorage || messages}
        defaultLocale="cs"
      >
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
        </Head>
        <div className="layout-wrapper">
          <div
            style={{ position: "absolute" }}
            dangerouslySetInnerHTML={{
              __html: `<!--${randomString}-->`,
            }}
          />
          {(() => {
            if (TypedComponent.getLayout) {
              return TypedComponent.getLayout(
                <TypedComponent {...pageProps} router={router} />,
                documentNonce
              );
            }
            if (errorRoutes.includes(router.route) || pageProps.pageNotFound) {
              return (
                <ErrorLayout>
                  <TypedComponent {...pageProps} router={router} />
                </ErrorLayout>
              );
            }

            return (
              <StandardLayout
                route={router.route}
                nonce={documentNonce}
                hideSearchForm={isLoginIframe}
              >
                {isLoginIframe && <LoginForm />}
                <TypedComponent
                  {...pageProps}
                  showLoginIframeTitle={isLoginIframe}
                  router={router}
                />
              </StandardLayout>
            );
          })()}
        </div>
      </IntlProvider>
    </Provider>
  );
}

async function downloadPages(urls, fileServerData, domain) {
  try {
    const result = {};

    if (!fileServerData) {
      return result;
    }

    const downloadPromises = urls
      .filter((url) => !!url)
      .map((url) => {
        let fileUrl = fileServerData[`data/${url}.html`];

        if (
          typeof window === "undefined" &&
          config.fileServerApiUrlInternal &&
          fileUrl &&
          fileUrl.includes("localhost:")
        ) {
          const urlObject = new URL(fileUrl);
          fileUrl = fileUrl.replace(
            `${urlObject.origin}/files/`,
            config.fileServerApiUrlInternal
          );
        }

        if (!fileUrl) {
          return null;
        }

        return timeBenchmark(
          async () => axios.get(fileUrl),
          null,
          `Downloading page: ${fileUrl} ${url} (${domain})`
        ).then((page) => ({
          url,
          data: page.data,
        }));
      });

    const fetchedFileServerPages = await timeBenchmark(
      async () => {
        const results = await Promise.all(downloadPromises);
        return results;
      },
      null,
      `Fetching File Server Pages (${domain})`
    );

    fetchedFileServerPages
      .filter((item): item is { url: string; data: any } => item != null)
      .forEach(({ url, data }) => {
        result[url] = data;
      });

    return result;
  } catch (e) {
    const failedRequestUrl = e?.config?.url;
    const failedPage =
      failedRequestUrl?.match(/data\/(.+)\.html$/)?.[1] ?? null;

    const errorDetails = {
      urls,
      fileServerData: fileServerData
        ? Object.fromEntries(
            urls.map((u) => [u, fileServerData[`data/${u}.html`] ?? null])
          )
        : null,
      failedPage,
      failedRequestUrl,
      errorMessage: e?.message,
      errorCode: e?.code,
      responseStatus: e?.response?.status,
    };

    Logger.error("File server page download failed", {
      cause: e instanceof Error ? e : undefined,
      requestEndpoint: "file-server-page",
      tags: { area: "setup" },
      extra: errorDetails,
    });
  }
}

async function loadStorageData({ domain, lang, reduxStore, req }) {
  if (!domain) {
    return;
  }

  if (reduxStore.getState().requestorConfig.fileServerData !== null) {
    return reduxStore.getState().requestorConfig.fileServerData;
  }

  const isDisabledDealer = reduxStore.getState().requestorConfig
    ?.isDisabledDealer;

  try {
    const domainForFileServer = getDomainForFileServer(domain);

    const isLocalhost = req?.headers.host?.includes("localhost:");

    const baseUrl =
      isLocalhost && config.fileServerApiUrlInternal
        ? config.fileServerApiUrlInternal
        : config.fileServerApiUrl;

    const fileServerUrlEnv = getRuntimeEnvValue(
      "NEXT_PUBLIC_D4_fileServerApiUrl"
    );
    const fileServerDeploymentId = getRuntimeEnvValue(
      "NEXT_PUBLIC_D4_deploymentId"
    );
    const urlToAsk = createFileServerIndexUrl(
      fileServerUrlEnv || baseUrl,
      domainForFileServer,
      lang === "cz" ? "cs" : lang,
      fileServerDeploymentId
    );

    if (lang) {
      const forwardedHeaders = req ? getForwardedHeadersFromRequest(req) : {};
      const fileServerData = await timeBenchmark(
        async () =>
          fetchFileServerData(
            urlToAsk,
            forwardedHeaders,
            fileServerDeploymentId
          ),
        null,
        `Fetching File Server Data (${domain})`
      );

      if (domain.includes("localhost") || domain.includes("d4:")) {
        // eslint-disable-next-line security/detect-non-literal-require -- this is only for localhost && no user input
        const defaultTextstorage = require(`./../../shared/lang/${
          lang === "cz" ? "cs" : lang
        }.json`);
        await reduxStore.dispatch(
          setRequestorConfig("textStorage", defaultTextstorage)
        );
      } else if (
        fileServerData &&
        typeof fileServerData["textstorage.json"] === "string"
      ) {
        let urlTextStorage = fileServerData["textstorage.json"];
        if (
          urlTextStorage.includes("https:") &&
          (urlTextStorage.includes("localhost") ||
            urlTextStorage.includes("ao3-resources"))
        ) {
          urlTextStorage = `http://${urlTextStorage.split("ttps://")[1]}`;
        }

        if (!urlTextStorage || urlTextStorage === "") {
          console.log(
            `No textstorage.json file found for agency ${domain}, probably wrongly configured.`
          );
        } else {
          const forwardedHeadersResolved = req
            ? getForwardedHeadersFromRequest(req)
            : {};
          const textStorageFetched = await timeBenchmark(
            async () =>
              axios.get(urlTextStorage, { headers: forwardedHeadersResolved }),
            null,
            "Fetching Text Storage"
          );

          const textStorage = textStorageFetched.data;
          await reduxStore.dispatch(
            setRequestorConfig("textStorage", textStorage)
          );
        }
      }

      const runtimeFileServerData = resolveFileServerDataForRuntime(
        fileServerData,
        domain
      );

      await reduxStore.dispatch(
        setRequestorConfig("fileServerData", runtimeFileServerData)
      );

      return runtimeFileServerData;
    }

    if (isDisabledDealer === false) {
      console.log(
        `Redirecting to 404 page - No language found for agency ${domain}, probably wrongly configured or requestor does not exists (legacy D3 web).`
      );
    }

    if (typeof window !== "undefined") {
      Router.push(ROUTES.NOT_FOUND);
    } else if (req?.res && !req.res.headersSent) {
      req.res.redirect(ROUTES.NOT_FOUND);
    }
  } catch (e) {
    if (isDisabledDealer === false) {
      if (!req?.res || !req.res.headersSent) {
        Logger.error(
          `Unable to load fileServer storage data for ${domain}. Maybe you have incorrect url?`,
          JSON.stringify(e)
        );
      }
    }
  }
}

function getCustomerToken(ctx) {
  const allCookies = nextCookie(ctx);

  if (!allCookies || !allCookies.d4customerToken) {
    return false;
  }

  return allCookies.d4customerToken;
}

function getCustomerDevice(ctx) {
  const allCookies = nextCookie(ctx);

  if (!allCookies || !allCookies.d4customerDevice) {
    return false;
  }

  return allCookies.d4customerDevice;
}

function getWebRequestorDetails({ menuLanguage, isDefaultLanguage, ctx }) {
  const requestorPublicKey = nextCookie(ctx)?.d4requestorPublicKey;
  const customerToken = nextCookie(ctx)?.d4customerToken;
  const clientId = getInitialClientId(ctx.req.hostname);

  const redirect404 = () => {
    if (ctx.res && !ctx.res.headersSent) {
      ctx.res.redirect(
        `${ROUTES.NOT_FOUND}?redirectDetail=not_configured_or_disabled_agency`
      );
    }
  };

  return loadWebRequestorDetails({
    selectedLanguage: isDefaultLanguage ? null : menuLanguage,
    golApiUrl: getGOLAPIUrl(ctx.req),
    requestorPublicKey,
    forcedCustomerToken: customerToken,
    forcedClientId: clientId,
    req: ctx.req,
    redirect404,
  });
}

MyApp.getInitialProps = async (props) => {
  const {
    Component,
    ctx,
    ctx: { reduxStore },
  } = props;
  const startTime = Date.now();
  const randomString = generateRandomString();

  let pageProps: IPageProps = {};
  const nonce = ctx?.res?.locals?.nonce;
  const { locale, messages } =
    ctx.req ||
    (typeof window !== "undefined" &&
      (window as any).__NEXT_DATA__.props.pageProps);

  // Always update the current time on page load/transition because the
  // <IntlProvider> will be a new instance even with pushState routing.
  const now = Date.now();

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
    let lang = reduxStore.getState().requestorConfig.currentLanguage; // default
    setLocale(lang);
    let { domain } = pageProps;
    const typedPageProps = pageProps as IPageProps & { statusCode?: number };
    const is404ErrorPage =
      ctx.pathname === ROUTES.NOT_FOUND ||
      (ctx.pathname === "/_error" &&
        Number(typedPageProps?.statusCode) === 404);

    const requestorClientId = getRequestorClientId(ctx);

    const { fileServerPagesToDownload = [] } = pageProps;

    /*
    Server side
     */
    if (ctx.req && !is404ErrorPage) {
      const forwardedHeaders = getForwardedHeadersFromRequest(ctx.req);
      let menuItemsLang = DEFAULT_LANG_GLOBAL;
      let isDefaultLang = true;
      if (
        ctx.req?.query?.lang !== undefined &&
        POSSIBLE_LANGUAGES.includes(ctx.req.query.lang)
      ) {
        setLocale(ctx.req.query.lang);
        lang = ctx.req.query.lang;
        menuItemsLang = ctx.req.query.lang;
        isDefaultLang = false;
      }

      await timeBenchmark(
        async () =>
          reduxStore.dispatch(
            getWebRequestorDetails({
              menuLanguage: menuItemsLang,
              isDefaultLanguage: isDefaultLang,
              ctx,
            })
          ),
        null,
        `Fetching Requestor Details (${domain})`
      );

      /*
      If isDefaultLang is true language is set based on first returned
      value from DetailRequestor SupportedLanguage attribute
       */
      if (isDefaultLang) {
        lang = reduxStore.getState().requestorConfig.currentLanguage;
        setLocale(lang);
      }

      await passwordProtection({ ctx, reduxStore, getGOLAPIUrl });

      if (ctx.res?.headersSent) {
        return { pageProps, randomString, locale, messages, now };
      }

      const restrictAirlines = reduxStore.getState().storage?.frontendSettings
        ?.carrierFilter;

      const cacheTTL = 24 * 60 * 60 * 1000;

      const [countries, airlines] = await Promise.all([
        fetchWithCache(
          "countries_cached",
          lang,
          cacheTTL,
          loadCountries(ctx.req, reduxStore.dispatch, lang),
          domain
        ),
        fetchWithCache(
          "airlines_cached",
          lang,
          cacheTTL,
          loadAirlines(ctx.req, restrictAirlines, reduxStore.dispatch, lang),
          domain
        ),
      ]);

      reduxStore.dispatch(setCountries(countries));
      reduxStore.dispatch(setAirlines(airlines));

      // check logged
      const customerToken = getCustomerToken(ctx);
      const customerDevice = getCustomerDevice(ctx);

      if (customerToken && customerDevice) {
        await setCustomerToken(customerToken);
        await setCustomerDevice(customerDevice);

        const clientId = getInitialClientId(ctx.req.hostname);

        const customerDetail = await timeBenchmark(
          async () =>
            detailCustomer2({
              customerToken,
              requestorPublicKey: nextCookie(ctx)?.d4requestorPublicKey,
              clientId,
              golApiUrl: getGOLAPIUrl(ctx.req),
              forwardedHeaders,
            }),
          null,
          `Fetching Customer Details (${domain})`
        );

        if (customerDetail.success) {
          await reduxStore.dispatch(
            setUserValues({
              isLoggedIn: true,
              ...customerDetail.data.formattedData,
            })
          );
        } else {
          await logoutCustomer();
          await reduxStore.dispatch(logoutUser());
        }
      } else if (
        reduxStore.getState().storage.frontendSettings.dealerCorporateSettings
          ?.enableAnonymousSearch === "false"
      ) {
        if (!ctx.req?.originalUrl?.includes(ROUTES.IFRAME)) {
          if (typeof window !== "undefined") {
            // Use Router for client-side navigation
            Router.push("/");
          } else if (
            ctx.req?.originalUrl?.includes(ROUTES.BOOKED) &&
            ctx.res &&
            !ctx.res.headersSent
          ) {
            ctx.res.redirect("/");
          }
        }
      }
    }

    if (!domain && process.browser) {
      domain = window.location.hostname;
    }

    const fileServerData = await loadStorageData({
      domain,
      lang,
      reduxStore,
      req: ctx.req,
    });

    const footerHTML = reduxStore.getState().requestorConfig?.footerInnerHTML;

    if (!footerHTML) {
      fileServerPagesToDownload.push("SF_STANDART_FOOTER");
    }

    const menuItems = reduxStore.getState().requestorConfig?.menuItems || [];

    if (
      shouldRenderFileServerPageNotFound({
        fileServerData,
        pageRoute: ROUTES.PAGE,
        pathname: ctx.pathname,
        queryUrl: ctx.query?.url,
      })
    ) {
      pageProps.pageNotFound = true;
      if (ctx.res && !ctx.res.headersSent) {
        ctx.res.statusCode = 404;
      }
    }

    if (menuItems.length > 0 && !pageProps.pageNotFound) {
      menuItems.forEach((menuItem) => {
        if (
          menuItem?.url &&
          !fileServerPagesToDownload.includes(menuItem.url)
        ) {
          fileServerPagesToDownload.push(menuItem.url);
        }
      });
    }

    const downloadedPages = await downloadPages(
      fileServerPagesToDownload,
      fileServerData,
      domain
    );

    if (downloadedPages) {
      reduxStore.dispatch(
        setRequestorConfig("downloadedPages", downloadedPages)
      );
      pageProps.downloadedPages = downloadedPages;
      if (downloadedPages?.SF_STANDART_FOOTER) {
        reduxStore.dispatch(
          setRequestorConfig("footerInnerHTML", {
            // @ts-ignore
            html: downloadedPages.SF_STANDART_FOOTER,
            nonce,
            clientId: requestorClientId,
          })
        );
      }
    }
  }

  pageProps.gtm = {
    ...pageProps.gtm,
    nonce,
  };

  const endTime = Date.now();
  const elapsedTime = endTime - startTime;

  if (elapsedTime > 0) {
    const renderType = ctx.req ? "Server-side" : "Client-side";
    const domain = pageProps?.domain || ctx?.req?.hostname || "unknown";
    console.log(
      `Time benchmark - ${domain} Website Initial Load (${renderType}) - FINISH. Total load time: ${elapsedTime}ms`
    );
  }

  return { pageProps, randomString, locale, messages, now };
};

export default withReduxStore(MyApp);
